import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ExternalLink, Clock, Zap, Award, Heart, DollarSign, Tag, TrendingUp, User as UserIcon, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../lib/api';

export function ModelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    loadModel();
    if (user) {
      checkFavorite();
    }
  }, [id, user]);

  const loadModel = async () => {
    try {
      const res = await axios.get(`${API}/models/${id}`);
      setModel(res.data);
    } catch (error) {
      console.error('Failed to load model:', error);
      toast.error('Modelo não encontrado');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const res = await axios.get(`${API}/favorites`, { withCredentials: true });
      setIsFavorite(res.data.some(f => f.id === id));
    } catch (error) {
      console.error('Failed to check favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`, { withCredentials: true });
        setIsFavorite(false);
        toast.success('Removido dos favoritos');
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, { withCredentials: true });
        setIsFavorite(true);
        toast.success('Adicionado aos favoritos');
      }
    } catch (error) {
      toast.error('Erro ao atualizar favoritos');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-pulse text-[#0066FF] text-xl">Carregando...</div>
        </div>
      </div>
    );
  }

  if (!model) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <main className="mt-16 pt-8 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              data-testid="model-images"
            >
              <div className="sticky top-24">
                <div className="aspect-video bg-[#121212] rounded-2xl overflow-hidden mb-4 border border-[#27272A]">
                  {model.images && model.images.length > 0 ? (
                    <img
                      src={model.images[selectedImage]}
                      alt={model.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      Sem imagem disponível
                    </div>
                  )}
                </div>
                {model.images && model.images.length > 1 && (
                  <div className="grid grid-cols-4 gap-3">
                    {model.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`aspect-video bg-[#121212] rounded-lg overflow-hidden border-2 transition-all ${
                          selectedImage === idx ? 'border-[#0066FF]' : 'border-[#27272A] hover:border-[#27272A]/50'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              data-testid="model-details"
              className="space-y-6"
            >
              <div>
                <div className="flex items-center space-x-3 mb-3">
                  {model.is_featured && (
                    <span className="bg-gradient-to-r from-[#0066FF] to-[#00FF99] text-black text-xs font-bold px-3 py-1 rounded-full">
                      DESTAQUE
                    </span>
                  )}
                  {model.is_premium && (
                    <span className="bg-[#00FF99] text-black text-xs font-bold px-3 py-1 rounded-full">
                      PREMIUM
                    </span>
                  )}
                </div>
                <h1 data-testid="model-title" className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-4">
                  {model.title}
                </h1>
                <p className="text-lg text-zinc-300 leading-relaxed">{model.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#121212] border border-[#27272A] rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-[#00FF99]" />
                    <span className="text-zinc-400 text-sm">Pontuação Viral</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{model.viral_score}/100</p>
                </div>
                <div className="bg-[#121212] border border-[#27272A] rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Award className="w-5 h-5 text-[#0066FF]" />
                    <span className="text-zinc-400 text-sm">Potencial de Venda</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{model.etsy_potential}/100</p>
                </div>
              </div>

              <div className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Especificações de Impressão</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <Clock className="w-4 h-4 text-[#0066FF]" />
                      <span className="text-zinc-400 text-sm">Tempo de Impressão</span>
                    </div>
                    <p className="text-white font-semibold">{model.print_time}</p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <Zap className="w-4 h-4 text-[#00FF99]" />
                      <span className="text-zinc-400 text-sm">Dificuldade</span>
                    </div>
                    <p className="text-white font-semibold">{model.difficulty}</p>
                  </div>
                  <div>
                    <span className="text-zinc-400 text-sm block mb-1">Filamento</span>
                    <p className="text-white font-semibold">{model.filament}</p>
                  </div>
                  <div>
                    <span className="text-zinc-400 text-sm block mb-1">Material</span>
                    <p className="text-white font-semibold">{model.material_usage}</p>
                  </div>
                  <div>
                    <span className="text-zinc-400 text-sm block mb-1">Suportes</span>
                    <p className="text-white font-semibold">{model.supports_needed ? 'Necessário' : 'Não necessário'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-400 text-sm block mb-1">Compatibilidade</span>
                    <p className="text-white font-semibold text-sm">{model.printer_compatibility}</p>
                  </div>
                  {(model.assembly_required === true || model.assembly_required === false) && (
                    <div>
                      <span className="text-zinc-400 text-sm block mb-1">Montagem Necessária</span>
                      <p className="text-white font-semibold">{model.assembly_required ? 'Sim' : 'Não'}</p>
                    </div>
                  )}
                </div>
              </div>

              {user?.is_premium && (
                <div className="backdrop-blur-xl bg-gradient-to-br from-[#0066FF]/10 to-[#00FF99]/10 border border-[#0066FF]/30 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <DollarSign className="w-5 h-5 text-[#00FF99]" />
                    <span>Inteligência Marketplace & Marketing</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <span className="text-zinc-400 text-sm block mb-1">Preço Sugerido</span>
                      <p className="text-white font-bold text-lg">{model.suggested_price}</p>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm block mb-1">Título Marketplace</span>
                      <p className="text-white font-semibold">{model.etsy_title}</p>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm block mb-2">Tags Marketplace</span>
                      <div className="flex flex-wrap gap-2">
                        {model.etsy_tags.map((tag, idx) => (
                          <span key={idx} className="bg-[#1A1A1A] border border-[#27272A] text-zinc-300 px-3 py-1 rounded-full text-sm">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-zinc-400 text-sm block mb-2">Ganchos TikTok</span>
                      <ul className="space-y-1">
                        {model.tiktok_hooks.map((hook, idx) => (
                          <li key={idx} className="text-white text-sm">• {hook}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-3 flex items-center space-x-2">
                  <UserIcon className="w-5 h-5 text-[#0066FF]" />
                  <span>Atribuição do Criador</span>
                </h3>
                <div className="space-y-2">
                  <p className="text-white"><span className="text-zinc-400">Criador:</span> <span className="font-semibold">{model.creator_name}</span></p>
                  <p className="text-white"><span className="text-zinc-400">Plataforma:</span> <span className="font-semibold">{model.creator_platform}</span></p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <a
                  href={model.download_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-button"
                  className="flex-1 flex items-center justify-center space-x-3 bg-[#0066FF] text-white hover:bg-[#3385FF] transition-all duration-300 font-bold tracking-wide rounded-lg px-6 py-3.5 shadow-[0_0_20px_rgba(0,102,255,0.4)] hover:shadow-[0_0_30px_rgba(0,102,255,0.6)]"
                >
                  <ExternalLink className="w-5 h-5" />
                  <span>Baixar STL Original</span>
                </a>
                <button
                  data-testid="favorite-button"
                  onClick={toggleFavorite}
                  className={`p-3.5 rounded-lg border transition-all ${
                    isFavorite
                      ? 'bg-red-500 border-red-500 text-white'
                      : 'bg-[#1A1A1A] border-[#27272A] text-zinc-400 hover:border-[#0066FF] hover:text-white'
                  }`}
                >
                  <Heart className={`w-6 h-6 ${isFavorite ? 'fill-current' : ''}`} />
                </button>
              </div>

              <div className="bg-[#0066FF]/10 border border-[#0066FF]/30 rounded-lg p-4">
                <p className="text-zinc-300 text-sm">
                  <strong className="text-white">Aviso Legal:</strong> Todos os arquivos STL pertencem aos seus criadores originais. 
                  BLUEVAULT apenas faz curadoria e redireciona para as fontes oficiais.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}